/**
 * MA-AN-131: Profit Path Performance Analytics
 * 
 * Strategic analytics layer above campaign-level traffic analytics.
 * Answers: "Which messaging strategies are most effective across all channels?"
 * 
 * Aggregates outcomes by profit_path_id using immutable snapshot fields.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Workflow,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Users,
  Target,
  AlertTriangle,
  Trophy,
  Filter,
  ChevronDown,
  Zap,
  BarChart3,
  AlertCircle,
  CheckCircle2,
  Rocket,
  ArrowUpRight,
  GitBranch,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { api } from '../../lib/api';

// =============================================================================
// API HOOKS
// =============================================================================

function useProfitPathPerformance({ period, sortBy, workspaceId, campaignId, vertical, channel, tenantId }) {
  return useQuery({
    queryKey: ['profit-path-performance', period, sortBy, workspaceId, campaignId, vertical, channel, tenantId],
    queryFn: async () => {
      const params = new URLSearchParams({ period, sort_by: sortBy });
      if (workspaceId) params.append('workspace_id', workspaceId);
      if (campaignId && campaignId !== 'all') params.append('campaign_id', campaignId);
      if (vertical && vertical !== 'all') params.append('vertical', vertical);
      if (channel && channel !== 'all') params.append('channel', channel);
      if (tenantId) params.append('tenant_id', tenantId);
      
      const res = await api.get(`/analytics/profit-path-performance?${params}`);
      return res.data;
    },
    staleTime: 60000,
    refetchOnWindowFocus: false,
  });
}

// =============================================================================
// FORMATTERS
// =============================================================================

function formatCurrency(value) {
  if (value === null || value === undefined) return '—';
  if (Math.abs(value) >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (Math.abs(value) >= 1000) return `$${(value / 1000).toFixed(1)}K`;
  return `$${value.toFixed(0)}`;
}

function formatPercent(value) {
  if (value === null || value === undefined) return '—';
  return `${value.toFixed(1)}%`;
}

// =============================================================================
// INSIGHT CARDS
// =============================================================================

function InsightCard({ title, value, subtitle, icon: Icon, color = 'indigo', highlight = false }) {
  const colorClasses = {
    indigo: 'text-indigo-400 bg-indigo-500/10 border-indigo-500/20',
    green: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
    amber: 'text-amber-400 bg-amber-500/10 border-amber-500/20',
    red: 'text-red-400 bg-red-500/10 border-red-500/20',
    purple: 'text-purple-400 bg-purple-500/10 border-purple-500/20',
    cyan: 'text-cyan-400 bg-cyan-500/10 border-cyan-500/20',
  };

  return (
    <div className={`rounded-lg p-4 border ${highlight ? 'ring-2 ring-offset-2 ring-offset-slate-900' : ''} ${colorClasses[color]}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs text-slate-400 uppercase tracking-wide">{title}</p>
          <p className="text-lg font-bold text-white mt-1 truncate max-w-[180px]" title={value}>
            {value || '—'}
          </p>
          {subtitle && (
            <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>
          )}
        </div>
        <Icon className={`w-5 h-5 flex-shrink-0 ${colorClasses[color].split(' ')[0]}`} />
      </div>
    </div>
  );
}

// =============================================================================
// PERFORMANCE STATUS BADGE
// =============================================================================

function PerformanceStatusBadge({ status }) {
  const configs = {
    strong: {
      label: 'Strong',
      icon: CheckCircle2,
      className: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    },
    scaling: {
      label: 'Scaling',
      icon: Rocket,
      className: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
    },
    needs_work: {
      label: 'Needs Work',
      icon: AlertTriangle,
      className: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    },
    neutral: {
      label: '',
      icon: null,
      className: '',
    },
  };

  const config = configs[status] || configs.neutral;
  
  if (!config.label) return null;
  
  const IconComponent = config.icon;
  
  return (
    <Badge variant="outline" className={`text-xs py-0.5 ${config.className}`}>
      {IconComponent && <IconComponent className="w-3 h-3 mr-1" />}
      {config.label}
    </Badge>
  );
}

// =============================================================================
// PERFORMANCE TABLE
// =============================================================================

function ProfitPathTable({ paths, sortBy, onSort, onPathClick }) {
  const SortIcon = ({ field }) => {
    if (sortBy !== field) return null;
    return <ChevronDown className="w-3.5 h-3.5 inline ml-1" />;
  };

  const SortableHeader = ({ field, children, className = "" }) => (
    <TableHead 
      className={`text-slate-300 cursor-pointer hover:text-white transition-colors ${className}`}
      onClick={() => onSort(field)}
    >
      {children}
      <SortIcon field={field} />
    </TableHead>
  );

  if (paths.length === 0) {
    return (
      <div className="py-10 text-center" data-testid="profit-path-perf-empty">
        <Workflow className="w-10 h-10 text-slate-600 mx-auto mb-3" />
        <h4 className="text-base font-medium text-slate-300 mb-1">No Profit Path Data</h4>
        <p className="text-slate-500 text-sm max-w-sm mx-auto">
          Profit path analytics appear when leads have profit_path_id set.
          Create profit paths and link funnels to start tracking.
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="border-slate-800 hover:bg-transparent">
            <TableHead className="text-slate-300 w-[200px]">Profit Path</TableHead>
            <TableHead className="text-slate-300">Message</TableHead>
            <TableHead className="text-right text-slate-300">Leads</TableHead>
            <TableHead className="text-right text-slate-300">Clients</TableHead>
            <SortableHeader field="revenue" className="text-right">Revenue</SortableHeader>
            <SortableHeader field="revenue_per_lead" className="text-right">Rev/Lead</SortableHeader>
            <TableHead className="text-right text-slate-300">L→A</TableHead>
            <TableHead className="text-right text-slate-300">A→S</TableHead>
            <SortableHeader field="conversion" className="text-right">S→C</SortableHeader>
            <SortableHeader field="efficiency" className="text-right">Efficiency</SortableHeader>
            <TableHead className="text-center text-slate-300">Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {paths.map((path) => (
            <TableRow 
              key={path.profit_path_id} 
              className={`border-slate-800/50 hover:bg-slate-800/30 cursor-pointer ${
                path.is_strongest ? 'bg-emerald-500/5' : 
                path.is_needs_work ? 'bg-amber-500/5' : ''
              }`}
              onClick={() => onPathClick?.(path.profit_path_id)}
              data-testid={`profit-path-row-${path.profit_path_id}`}
            >
              <TableCell className="font-medium">
                <div className="flex items-center gap-2">
                  <Workflow className="w-4 h-4 text-purple-400 flex-shrink-0" />
                  <span className="text-slate-100 truncate max-w-[160px]" title={path.profit_path_name}>
                    {path.profit_path_name}
                  </span>
                  {path.is_strongest && (
                    <Trophy className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" title="Strongest" />
                  )}
                  {path.is_best_conversion && !path.is_strongest && (
                    <Zap className="w-3.5 h-3.5 text-purple-400 flex-shrink-0" title="Best Conversion" />
                  )}
                </div>
              </TableCell>
              <TableCell>
                <div className="flex flex-col">
                  <span className="text-xs text-slate-400 capitalize">
                    {path.message_category?.replace(/_/g, " ") || "—"}
                  </span>
                  <span className="text-slate-200 text-sm truncate max-w-[120px]" title={path.message_variant}>
                    {path.message_variant || "—"}
                  </span>
                </div>
              </TableCell>
              <TableCell className="text-right font-mono text-slate-100">{path.leads}</TableCell>
              <TableCell className="text-right font-mono text-slate-100">{path.clients}</TableCell>
              <TableCell className="text-right font-mono text-emerald-400">{formatCurrency(path.revenue)}</TableCell>
              <TableCell className="text-right font-mono text-slate-100">
                {formatCurrency(path.revenue_per_lead)}
              </TableCell>
              <TableCell className="text-right font-mono text-slate-100">
                {formatPercent(path.lead_to_appointment_rate)}
              </TableCell>
              <TableCell className="text-right font-mono text-slate-100">
                {formatPercent(path.appointment_to_show_rate)}
              </TableCell>
              <TableCell className="text-right font-mono text-slate-100">
                {formatPercent(path.show_to_close_rate)}
              </TableCell>
              <TableCell className="text-right font-mono text-slate-100">
                {formatCurrency(path.capital_efficiency)}
              </TableCell>
              <TableCell className="text-center">
                <PerformanceStatusBadge status={path.performance_status} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export default function ProfitPathPerformance({ 
  period = 'mtd', 
  workspaceId, 
  campaignId,
  vertical,
  channel,
  tenantId,
  onPathClick,
  compact = false,
}) {
  const [sortBy, setSortBy] = useState('revenue');
  
  const query = useProfitPathPerformance({
    period,
    sortBy,
    workspaceId,
    campaignId,
    vertical,
    channel,
    tenantId,
  });

  const paths = query.data?.paths || [];
  const insights = query.data?.insights || {};

  if (compact) {
    // Compact view for embedding in detail pages
    return (
      <div className="space-y-3" data-testid="profit-path-performance-compact">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium text-slate-300">Performance Summary</h4>
          <span className="text-xs text-slate-500">{period.toUpperCase()}</span>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div className="bg-slate-800/50 rounded-lg p-3">
            <p className="text-xs text-slate-400">Total Leads</p>
            <p className="text-lg font-bold text-white">{query.data?.total_leads || 0}</p>
          </div>
          <div className="bg-slate-800/50 rounded-lg p-3">
            <p className="text-xs text-slate-400">Revenue</p>
            <p className="text-lg font-bold text-emerald-400">{formatCurrency(query.data?.total_revenue || 0)}</p>
          </div>
          <div className="bg-slate-800/50 rounded-lg p-3">
            <p className="text-xs text-slate-400">Clients</p>
            <p className="text-lg font-bold text-white">{query.data?.total_clients || 0}</p>
          </div>
          <div className="bg-slate-800/50 rounded-lg p-3">
            <p className="text-xs text-slate-400">Avg Rev/Path</p>
            <p className="text-lg font-bold text-white">{formatCurrency(query.data?.avg_revenue_per_path || 0)}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="profit-path-performance">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Workflow className="w-5 h-5 text-purple-400" />
            Profit Path Performance
          </h3>
          <p className="text-sm text-slate-400 mt-1">
            Strategic view of messaging effectiveness across all channels
          </p>
        </div>
      </div>

      {/* Insight Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <InsightCard
          title="Strongest Path"
          value={insights.strongest_path_name}
          subtitle={insights.strongest_revenue ? formatCurrency(insights.strongest_revenue) + ' revenue' : null}
          icon={Trophy}
          color="green"
          highlight={!!insights.strongest_path_name}
        />
        <InsightCard
          title="Best Conversion"
          value={insights.best_conversion_path_name}
          subtitle={insights.best_conversion_rate ? formatPercent(insights.best_conversion_rate) + ' close rate' : null}
          icon={TrendingUp}
          color="purple"
        />
        <InsightCard
          title="Most Efficient"
          value={insights.most_efficient_path_name}
          subtitle={insights.best_efficiency ? formatCurrency(insights.best_efficiency) + '/lead' : null}
          icon={Zap}
          color="cyan"
        />
        <InsightCard
          title="Needs Attention"
          value={insights.needs_attention_path_name}
          subtitle={insights.needs_attention_issue}
          icon={AlertTriangle}
          color="amber"
        />
      </div>

      {/* Summary Stats */}
      <div className="flex items-center gap-6 text-sm text-slate-400">
        <span className="flex items-center gap-1.5">
          <Workflow className="w-4 h-4" />
          <strong className="text-slate-200">{query.data?.total_paths || 0}</strong> paths
        </span>
        <span className="flex items-center gap-1.5">
          <Users className="w-4 h-4" />
          <strong className="text-slate-200">{query.data?.total_leads || 0}</strong> leads
        </span>
        <span className="flex items-center gap-1.5">
          <CheckCircle2 className="w-4 h-4" />
          <strong className="text-slate-200">{query.data?.total_clients || 0}</strong> clients
        </span>
        <span className="flex items-center gap-1.5">
          <DollarSign className="w-4 h-4" />
          <strong className="text-emerald-400">{formatCurrency(query.data?.total_revenue || 0)}</strong> revenue
        </span>
      </div>

      {/* Sort Control */}
      <div className="flex justify-end">
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-48 bg-slate-800 border-slate-700 h-8 text-sm">
            <Filter className="w-3.5 h-3.5 mr-2 text-slate-400" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="revenue">Revenue</SelectItem>
            <SelectItem value="profit">Profit</SelectItem>
            <SelectItem value="revenue_per_lead">Revenue/Lead</SelectItem>
            <SelectItem value="conversion">Close Rate</SelectItem>
            <SelectItem value="efficiency">Capital Efficiency</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Performance Table */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardContent className="p-0">
          {query.isLoading ? (
            <div className="py-10 text-center text-slate-400">
              <div className="animate-pulse">Loading profit path performance...</div>
            </div>
          ) : query.error ? (
            <div className="py-10 text-center">
              <GitBranch className="w-10 h-10 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-300 font-medium text-sm">Profit Path Performance Unavailable</p>
              <p className="text-slate-500 text-xs mt-1">Unable to load profit path performance data at this time.</p>
            </div>
          ) : (
            <ProfitPathTable 
              paths={paths} 
              sortBy={sortBy}
              onSort={setSortBy}
              onPathClick={onPathClick}
            />
          )}
        </CardContent>
      </Card>

      {/* Data Note */}
      <div className="text-xs text-slate-500 text-center">
        * Uses snapshotted profit_path_id values. Records without profit_path_id are excluded.
      </div>
    </div>
  );
}
